import { QueryClient, QueryClientConfig } from '@tanstack/react-query';

/**
 * React Query configuration for SHARP
 *
 * This configuration sets up:
 * - Automatic request deduplication (multiple components requesting same data = 1 API call)
 * - Smart caching with stale-while-revalidate pattern
 * - Background refetching for fresh data
 * - Error retry logic with exponential backoff
 */
export const queryClientConfig: QueryClientConfig = {
  defaultOptions: {
    queries: {
      // How long data is considered "fresh" (won't refetch during this time)
      staleTime: 5 * 60 * 1000, // 5 minutes

      // How long inactive data stays in cache before garbage collection
      gcTime: 10 * 60 * 1000, // 10 minutes (formerly cacheTime)

      // Disable refetch on window focus — avoids bursting many concurrent HTTP requests
      // every time the user switches back to the tab, which can exhaust network connections
      // over an extended session and contribute to browser IO thread crashes.
      refetchOnWindowFocus: false,

      // Refetch when component remounts if data is stale
      refetchOnMount: true,

      // Don't refetch on network reconnect (can be enabled if needed)
      refetchOnReconnect: false,

      // Retry failed requests (exponential backoff)
      retry: (failureCount, error: any) => {
        // Don't retry on 4xx errors (client errors)
        if (error?.status >= 400 && error?.status < 500) {
          return false;
        }
        // Retry up to 2 times for server errors
        return failureCount < 2;
      },

      // Delay between retries (exponential backoff)
      retryDelay: (attemptIndex) => Math.min(1000 * 2 ** attemptIndex, 30000),
    },
    mutations: {
      // Retry mutations once on failure
      retry: 1,
    },
  },
};

/**
 * Create a new QueryClient instance with the SHARP configuration
 */
export function createQueryClient(): QueryClient {
  return new QueryClient(queryClientConfig);
}

/**
 * Query key factory for consistent cache key management
 *
 * Usage:
 * - queryKeys.organizations.all() -> ['organizations']
 * - queryKeys.organizations.detail(id) -> ['organizations', id]
 * - queryKeys.organizations.teams(id) -> ['organizations', id, 'teams']
 */
export const queryKeys = {
  // User-related queries
  users: {
    all: () => ['users'] as const,
    detail: (id: string) => ['users', id] as const,
    current: () => ['users', 'current'] as const,
    organizations: (userId?: string) =>
      userId ? ['users', userId, 'organizations'] as const : ['users', 'organizations'] as const,
    teams: (userId?: string) =>
      userId ? ['users', userId, 'teams'] as const : ['users', 'teams'] as const,
    memberships: (userId?: string) =>
      userId ? ['users', userId, 'memberships'] as const : ['users', 'memberships'] as const,
    invitations: (userId?: string) =>
      userId ? ['users', userId, 'invitations'] as const : ['users', 'invitations'] as const,
  },

  // Organization-related queries
  organizations: {
    all: () => ['organizations'] as const,
    detail: (id: string) => ['organizations', id] as const,
    teams: (orgId: string) => ['organizations', orgId, 'teams'] as const,
    members: (orgId: string) => ['organizations', orgId, 'members'] as const,
    invitations: (orgId: string) => ['organizations', orgId, 'invitations'] as const,
    subscriptions: (orgId: string) => ['organizations', orgId, 'subscriptions'] as const,
  },

  // Team-related queries
  teams: {
    all: () => ['teams'] as const,
    detail: (id: string) => ['teams', id] as const,
    members: (teamId: string) => ['teams', teamId, 'members'] as const,
    content: (teamId: string) => ['teams', teamId, 'content'] as const,
    sessions: (teamId: string) => ['teams', teamId, 'sessions'] as const,
  },

  // Content-related queries
  content: {
    all: () => ['content'] as const,
    detail: (id: string) => ['content', id] as const,
    byTeam: (teamId: string) => ['content', 'team', teamId] as const,
    byOrg: (orgId: string) => ['content', 'org', orgId] as const,
  },

  // Subscription-related queries
  subscriptions: {
    all: () => ['subscriptions'] as const,
    detail: (id: string) => ['subscriptions', id] as const,
    byOrg: (orgId: string) => ['subscriptions', 'org', orgId] as const,
  },

  // Invitation-related queries
  invitations: {
    all: () => ['invitations'] as const,
    detail: (id: string) => ['invitations', id] as const,
    pending: () => ['invitations', 'pending'] as const,
  },
} as const;
